import React, { JSXElementConstructor, ReactElement } from 'react';
import {
  Flex,
  Link as ChakraLink,
  LinkProps as ChakraLinkProps,
  IconProps,
  forwardRef,
} from '@chakra-ui/react';
import {
  getLinkTextDecorationStyle,
  getLinkFlexStyles,
  getIconFlexStyles,
} from './link.theme';
import {
  ColorMode,
  useColorMode,
} from '@allergan-data-labs/alle-elements-core';

export type LinkVariants = 'brand' | 'action';

export interface LinkProps extends ChakraLinkProps {
  children?: React.ReactNode;
  /**
   * The visual style of the Link.
   * @default brand
   */
  variant?: LinkVariants;
  /**
   * If added, the Link will show an icon after the text.
   */
  icon?: ReactElement<IconProps, string | JSXElementConstructor<IconProps>>;
  /** The color mode that the button will display.
   * @default light
   */
  colorMode?: ColorMode;
  /**
   * if set to true, the Link will have no underline on stand-alone.
   * @default false
   */
  isStandalone?: boolean;
}

/**
 *  Link is an accessible element for navigation.
 */
export const Link = forwardRef<LinkProps, 'a'>(
  (
    {
      isStandalone = false,
      icon,
      children,
      colorMode: propColorMode,
      ...props
    },
    ref
  ) => {
    /* 
      The colorScheme value is populated by the colorMode prop. Had to do it
      this way because we cannot extend the chakra-ui component props and
      we kept getting an error for using custom props on a chakra component.
      Keep in mind that not all of our themed library components require
      this method. We also could not use the colorScheme property for our color
      mode changes because certain components, like button, was already using
      the colorScheme property.
  */
    const { colorMode } = useColorMode(propColorMode);
    return (
      <ChakraLink
        ref={ref}
        colorScheme={colorMode}
        sx={getLinkTextDecorationStyle({ isStandalone })}
        {...props}
      >
        {icon ? (
          <Flex sx={getLinkFlexStyles()}>
            {children}
            <Flex sx={getIconFlexStyles()}>{icon}</Flex>
          </Flex>
        ) : (
          children
        )}
      </ChakraLink>
    );
  }
);
